/**
 * Shows a plugin's block or view: runs its handler in the sandbox, draws the
 * building blocks it returns, runs its actions (buttons, forms) and draws again.
 * Redraws by itself, debounced, when tasks or pages change.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sanitizeUi, type InstalledPlugin, type UiNode } from '@nexus/plugins';
import { useStore } from '../db/context';
import { useDescribe } from '../ai/describe';
import { useUi } from '../state/ui';
import { cn } from '../lib/cn';
import { sandboxFor, usePluginLog } from './runtime';

const REDRAW_MS = 1_000;

export function PluginSurface({
  plugin,
  kind,
  id,
  dryRun,
}: {
  plugin: InstalledPlugin;
  kind: 'block' | 'view';
  id: string;
  /** Builder preview: changes are shown, not made. */
  dryRun?: boolean;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const describe = useDescribe();
  const [ui, setUi] = useState<UiNode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);

  const render = useCallback(async () => {
    const mine = ++generation.current;
    try {
      const box = await sandboxFor(store, plugin, describe, { dryRun });
      const out = sanitizeUi(await box.run(kind, id));
      if (mine === generation.current) {
        setUi(out);
        setError(null);
      }
    } catch (e) {
      if (mine === generation.current) setError(e instanceof Error ? e.message : String(e));
    }
    // describe changes identity per render; the plugin version is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, plugin.pageId, plugin.updatedAt, kind, id, dryRun]);

  useEffect(() => {
    const first = setTimeout(() => void render());
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = store.subscribe(() => {
      clearTimeout(timer);
      timer = setTimeout(() => void render(), REDRAW_MS);
    }, ['tasks', 'pages', 'props']);
    return () => {
      clearTimeout(first);
      clearTimeout(timer);
      off();
    };
  }, [render, store]);

  const act = async (action: string, payload: unknown) => {
    setBusy(true);
    try {
      const box = await sandboxFor(store, plugin, describe, { dryRun });
      await box.run('action', action, [payload]);
      await render();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      usePluginLog.getState().add(plugin.pageId, msg);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (error)
    return (
      <p role="alert" className="text-sm text-danger" data-testid="plugin-error">
        {t('plugins.error', { message: error })}
      </p>
    );
  if (!ui) return <p className="text-sm text-fg-muted">{t('app.loading')}</p>;
  return (
    <div className={cn('text-sm', busy && 'opacity-60')} data-testid="plugin-surface">
      <Node node={ui} act={act} />
    </div>
  );
}

function Node({ node, act }: { node: UiNode; act: (a: string, p: unknown) => void }) {
  const open = (pageId: string) => useUi.getState().open({ kind: 'page', pageId });
  switch (node.type) {
    case 'stack':
      return (
        <div className="flex flex-col gap-2">
          {node.children.map((c, i) => (
            <Node key={i} node={c} act={act} />
          ))}
        </div>
      );
    case 'row':
      return (
        <div className="flex flex-wrap items-center gap-2">
          {node.children.map((c, i) => (
            <Node key={i} node={c} act={act} />
          ))}
        </div>
      );
    case 'heading':
      return <h3 className="font-semibold">{node.text}</h3>;
    case 'text':
      return (
        <p className={cn('whitespace-pre-wrap', node.muted && 'text-fg-muted')}>{node.text}</p>
      );
    case 'badge':
      return <span className="rounded bg-bg-subtle px-1.5 py-0.5 text-xs">{node.text}</span>;
    case 'link':
      return (
        <button
          type="button"
          onClick={() => open(node.pageId)}
          className="text-left text-accent-ink hover:underline"
        >
          {node.text}
        </button>
      );
    case 'list':
      return (
        <ul className="flex flex-col divide-y divide-border">
          {node.items.map((it, i) => (
            <li key={i} className="flex items-baseline gap-2 py-1">
              {it.pageId ? (
                <button
                  type="button"
                  onClick={() => open(it.pageId!)}
                  className="min-w-0 flex-1 truncate text-left hover:underline"
                >
                  {it.text}
                </button>
              ) : (
                <span className="min-w-0 flex-1 truncate">{it.text}</span>
              )}
              {it.secondary && (
                <span className="shrink-0 text-xs text-fg-muted">{it.secondary}</span>
              )}
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            {node.columns.length > 0 && (
              <thead>
                <tr className="border-b border-border text-xs text-fg-muted">
                  {node.columns.map((c, i) => (
                    <th key={i} className="px-2 py-1 font-medium">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {node.rows.map((r, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  {r.map((c, j) => (
                    <td key={j} className="px-2 py-1">
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'progress':
      return (
        <div className="flex flex-col gap-1">
          {node.label && <span className="text-xs text-fg-muted">{node.label}</span>}
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(node.value * 100)}
            aria-label={node.label || undefined}
            className="h-2 overflow-hidden rounded-full bg-bg-subtle"
          >
            <div className="h-full bg-accent" style={{ width: `${node.value * 100}%` }} />
          </div>
        </div>
      );
    case 'bars': {
      const max = Math.max(1, ...node.items.map((i) => i.value));
      return (
        <div className="flex flex-col gap-1">
          {node.items.map((it, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-28 shrink-0 truncate text-xs">{it.label}</span>
              <div className="h-3 flex-1 rounded bg-bg-subtle">
                <div
                  className="h-full rounded bg-accent"
                  style={{ width: `${(it.value / max) * 100}%` }}
                />
              </div>
              <span className="w-10 shrink-0 text-right text-xs tabular-nums">{it.value}</span>
            </div>
          ))}
        </div>
      );
    }
    case 'button':
      return (
        <button
          type="button"
          onClick={() => act(node.action, node.payload)}
          className="self-start rounded-lg border border-border px-2 py-1 hover:bg-bg-hover"
        >
          {node.label}
        </button>
      );
    case 'form':
      return <Form node={node} act={act} />;
  }
}

function Form({
  node,
  act,
}: {
  node: Extract<UiNode, { type: 'form' }>;
  act: (a: string, p: unknown) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        act(node.action, values);
        setValues({});
      }}
    >
      {node.fields.map((f) => (
        <label key={f.name} className="flex flex-col gap-1 text-xs">
          {f.label}
          <input
            type={f.kind}
            value={values[f.name] ?? ''}
            onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
            className="rounded-lg border border-border bg-bg px-2 py-1 text-sm"
          />
        </label>
      ))}
      <button type="submit" className="rounded-full bg-accent px-2 py-1 font-medium text-accent-fg">
        {node.submit}
      </button>
    </form>
  );
}
